import { useBuildHandoffUrl } from '@cma/common'
import { getAccessTokens, useLogout } from '@cma/features/auth'
import { HelixIcon } from '@helix/helix-icon'
import {
  circle_question as CircleQuestion,
  sparkle as Sparkle
} from '@helix/helix-icon/outlined'
import {
  ClientSwitcher,
  Navbar,
  NavbarAdditionalItems,
  NavbarBody,
  NavbarNavigationBar,
  NavbarUserMenu
} from '@helix/navbar'
import {
  AihubChatLayout,
  AihubChatProvider,
  useChatWidget
} from '@lonewolf/aihub-chat'
import { useQuery } from '@tanstack/react-query'
import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styles from './AihubChat.module.css'
import { loadPlatformService } from './platformService'
import { UserRole, useCurrentUser } from './useCurrentUser'

interface NavigationProps {
  hideClientSwitcher?: boolean
  navbody?: React.ReactNode
  style?: React.CSSProperties
}

export function Navigation({
  hideClientSwitcher = false,
  navbody,
  style
}: NavigationProps) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: clientSwitcher } = useClientSwitcher()
  const buildHandoffUrl = useBuildHandoffUrl()
  const logout = useLogout()
  const accessTokens = getAccessTokens()
  const [navRef, navHeight] = useElementHeight<HTMLDivElement>()

  const hasAspenChat = !!currentUser?.features?.hasAspenChat

  const navbar = (
    <>
      <div ref={navRef}>
        <Navbar
          disablePendo
          dataLwtId="default"
          logoUrl={buildHandoffUrl('/')}
          hideLogo={hideClientSwitcher}
          environment={getEnvFromHost()}
          applicationCode="cc"
          applicationName="Cloud CMA"
          style={style}
          activeClientId={clientSwitcher?.activeClientId}>
          {!hideClientSwitcher && clientSwitcher && (
            <ClientSwitcher platformService={clientSwitcher?.platformService} />
          )}
          {navbody ? (
            <NavbarBody>{navbody}</NavbarBody>
          ) : (
            <NavbarNavigationBar
              navItems={[
                { label: 'Home', url: '/' },
                { label: 'CMA', url: '/cmas' },
                { label: 'Buyer Tour', url: '/tours' },
                { label: 'Property', url: '/properties' },
                currentUser?.features?.hasPresentation
                  ? { label: 'Presentation', url: '/presentations' }
                  : undefined,
                currentUser?.features?.hasFlyers
                  ? { label: 'Flyer', url: '/flyers' }
                  : undefined,
                currentUser?.features?.hasDocuments
                  ? { label: 'Documents', url: '/documents' }
                  : undefined,
                currentUser?.features?.hasHomebeat ||
                currentUser?.features?.offerHomebeat
                  ? { label: 'Homebeat', url: '/homebeats' }
                  : undefined
              ].filter(
                (item): item is { label: string; url: string } => !!item
              )}
              linkWrapper={({ item }) => {
                if (
                  item.url ===
                  `${process.env.VITE_APP_CMA_URL}/admin/account/${currentUser?.account?.id}`
                ) {
                  return <a href={item.url}>{item.label}</a>
                }

                const isExternal = [
                  'Learning Resources',
                  'Support Center'
                ].includes(`${item.label}`)

                if (isExternal) {
                  return (
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {item.label}
                    </a>
                  )
                }

                return <Link to={item.url || ''}>{item.label}</Link>
              }}
            />
          )}
          <NavbarAdditionalItems>
            {hasAspenChat && <AihubChatButton />}
            <button className="helix-unified-nav__nav-button helix-unified-nav__nav-button-help">
              <HelixIcon icon={CircleQuestion} />
            </button>
            <NavbarUserMenu
              user={{
                name: currentUser?.name || 'Uknowon',
                email: currentUser?.email || 'Uknowon',
                avatarURL: currentUser?.avatar || undefined
              }}
              menuItems={[
                { label: 'Account Settings', url: '/settings' },
                currentUser?.hasPlatformDashboard &&
                !accessTokens?.impersonateToken
                  ? { label: 'Admin Tools', url: '/admin/settings' }
                  : {},
                (currentUser?.role || 0) >= UserRole.ADMIN_PLUS
                  ? { label: 'Brokers', url: '/admin/brokers' }
                  : currentUser?.features?.hasBrokerAccess
                  ? { label: 'Dashboard', url: '/dashboard' }
                  : // Old dashboard for backward compatibility
                  currentUser?.isAdmin && currentUser.account?.isSmallBroker
                  ? {
                      label: 'Dashboard',
                      url: `${process.env.VITE_APP_CMA_URL}/admin/account/${currentUser.account?.id}`
                    }
                  : {},
                { divider: true },
                { label: 'Custom Pages', url: '/settings/custom-pages' },
                currentUser?.features?.hasApiButton
                  ? {
                      label: 'Lead Generation',
                      url: '/settings/lead-generation'
                    }
                  : {},
                { divider: true },
                {
                  label: 'Learning Resources',
                  url: 'https://learn.cloudagentsuite.com/videos-cma'
                },
                {
                  label: 'Support Center',
                  url: 'https://lonewolf.my.site.com/s/contactsupport'
                },
                { divider: true },
                {
                  label: accessTokens?.impersonateToken
                    ? 'Back to your account'
                    : 'Sign Out',
                  onClick: () => logout({ shouldUseCmaToLogout: true })
                }
              ].filter((item) => !!Object.keys(item).length)}
              linkWrapper={({ item }) => {
                if (
                  item.url ===
                  `${process.env.VITE_APP_CMA_URL}/admin/account/${currentUser?.account?.id}`
                ) {
                  return <a href={item.url}>{item.label}</a>
                }

                const isExternal = [
                  'Learning Resources',
                  'Support Center'
                ].includes(`${item.label}`)

                if (isExternal) {
                  return (
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {item.label}
                    </a>
                  )
                }

                return <Link to={item.url || ''}>{item.label}</Link>
              }}
            />
          </NavbarAdditionalItems>
        </Navbar>
      </div>
      {hasAspenChat && (
        <div className={styles.overlay} style={{ top: navHeight }}>
          <AihubChatLayout>{null}</AihubChatLayout>
        </div>
      )}
    </>
  )

  if (!hasAspenChat) return navbar

  return (
    <AihubChatProvider
      launcher
      accessToken={currentUser?.lwaJwt || ''}
      activeClientId={clientSwitcher?.activeClientId}
      serviceUrl={process.env.VITE_APP_AIHUB_URL}
      currentApp="cc">
      {navbar}
    </AihubChatProvider>
  )
}

function AihubChatButton() {
  const { status, openDraft, minimize, restore } = useChatWidget()

  function handleClick() {
    if (status === 'open') return minimize()
    if (status === 'minimized') return restore()

    return openDraft()
  }

  return (
    <button
      type="button"
      title="Ask Aspen"
      aria-label="Ask Aspen"
      aria-expanded={status === 'open'}
      className="helix-unified-nav__nav-button"
      onClick={handleClick}>
      <HelixIcon icon={Sparkle} />
    </button>
  )
}

function useElementHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return

    function measure() {
      const el = ref.current
      if (el) setHeight(el.getBoundingClientRect().height)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return [ref, height] as const
}

export function useClientSwitcher() {
  const { data: { currentUser } = {} } = useCurrentUser()
  const lwaJwt = currentUser?.lwaJwt || ''

  return useQuery(
    ['clientSwitcher', { lwaJwt }],
    () =>
      loadPlatformService({
        lwDomain: process.env.VITE_APP_LW_URL,
        token: lwaJwt
      }),
    {
      suspense: false,
      retry: false,
      enabled: !!lwaJwt,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  )
}

function getEnvFromHost() {
  const hostname = window.location.hostname.toLowerCase()

  if (hostname.includes('localhost')) return 'dev'
  if (hostname.includes('staging')) return 'stg'

  return 'prod'
}
